// composite.error-banner.v1
//
// One canonical surface for "something went wrong" / "heads up" /
// "you should know this" notices that aren't blocking. Sits above
// content. Dismissible variant has a close button.

import { type JSX, Show, splitProps } from "solid-js"
import { Icon, type IconProps } from "../../components/icon"
import type { NoticeIntent } from "../types"

export interface ErrorBannerProps {
  intent?: NoticeIntent
  title: string
  description?: JSX.Element
  icon?: IconProps["name"]
  action?: JSX.Element
  dismissible?: boolean
  onDismiss?: () => void
  class?: string
}

const toneClass = {
  info: "border-icon-info-base/30 bg-surface-info-weak text-text-base",
  success: "border-icon-success-base/30 bg-surface-success-weak text-text-base",
  warning: "border-icon-warning-base/30 bg-surface-warning-weak text-text-base",
  error: "border-icon-critical-base/30 bg-surface-critical-weak text-text-base",
} as const satisfies Record<NoticeIntent, string>

const iconClass = {
  info: "text-icon-info-base",
  success: "text-icon-success-base",
  warning: "text-icon-warning-base",
  error: "text-icon-critical-base",
} as const satisfies Record<NoticeIntent, string>

const defaultIcon = {
  info: "help",
  success: "circle-check",
  warning: "warning",
  error: "circle-exclamation",
} as const satisfies Record<NoticeIntent, IconProps["name"]>

export function ErrorBanner(props: ErrorBannerProps) {
  const [local, rest] = splitProps(props, [
    "intent",
    "title",
    "description",
    "icon",
    "action",
    "dismissible",
    "onDismiss",
    "class",
  ])
  const intent = local.intent ?? "info"

  return (
    <div
      role="status"
      data-registry-id="composite.error-banner"
      data-intent={intent}
      classList={{
        "flex items-start gap-3 rounded-lg border px-3.5 py-3": true,
        [toneClass[intent]]: true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <Icon
        name={local.icon ?? defaultIcon[intent]}
        size="small"
        class={`shrink-0 mt-0.5 ${iconClass[intent]}`}
      />
      <div class="min-w-0 flex-1 flex flex-col gap-1">
        <div class="text-14-medium text-text-strong">{local.title}</div>
        <Show when={local.description}>
          <div class="text-13-regular text-text-base">{local.description}</div>
        </Show>
      </div>
      <Show when={local.action}>
        <div class="shrink-0">{local.action}</div>
      </Show>
      <Show when={local.dismissible}>
        <button
          type="button"
          class="shrink-0 rounded-md p-1 text-text-weak hover:text-text-strong"
          aria-label="Dismiss"
          onClick={local.onDismiss}
        >
          <Icon name="close-small" size="small" />
        </button>
      </Show>
    </div>
  )
}
